import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  Briefcase,
  FileText,
  Calendar,
  Star,
  Users,
  TrendingUp,
  Mail,
  Clock,
  UserCheck,
  BarChart3
} from "lucide-react";

export function RecruitmentPage() {
  const features = [
    {
      icon: Briefcase,
      title: "Job Posting Management",
      description: "Create and publish job listings with custom branding. Share across multiple platforms with one click."
    },
    {
      icon: FileText,
      title: "Custom Application Forms",
      description: "Build tailored application forms with your own questions. Collect exactly the information you need."
    },
    {
      icon: Calendar,
      title: "Interview Scheduling",
      description: "Coordinate interviews effortlessly with built-in calendar integration. Send automated reminders to candidates."
    },
    {
      icon: Star,
      title: "Candidate Evaluation",
      description: "Rate and review candidates with customizable scoring. Compare applicants side-by-side to find the best fit."
    },
    {
      icon: UserCheck,
      title: "Onboarding Workflows",
      description: "Streamline new hire onboarding with automated checklists and document collection."
    },
    {
      icon: TrendingUp,
      title: "Recruitment Analytics",
      description: "Track application funnels, time-to-hire, and source effectiveness. Optimize your hiring process with data."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Post a Job",
      description: "Create a job listing with description, requirements, and application form",
      icon: Briefcase
    },
    {
      number: 2,
      title: "Review Applications",
      description: "Receive and evaluate applications in an organized dashboard",
      icon: FileText
    },
    {
      number: 3,
      title: "Schedule Interviews",
      description: "Coordinate interviews with top candidates using built-in scheduling",
      icon: Calendar
    },
    {
      number: 4,
      title: "Onboard New Hires",
      description: "Convert successful candidates into employees with automated onboarding",
      icon: UserCheck
    }
  ];

  const relatedFeatures = [
    {
      title: "Employee & Workforce",
      description: "Seamlessly transition new hires into your workforce management system",
      icon: Users,
      link: "/features/workforce"
    },
    {
      title: "Marketing & Communications",
      description: "Promote job openings through email campaigns",
      icon: Mail,
      link: "/features/marketing"
    },
    {
      title: "Analytics & Insights",
      description: "Analyze recruitment metrics and hiring funnel performance",
      icon: BarChart3,
      link: "/features/analytics"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Streamline Your Recruitment & Hiring"
        subtitle="From job posting to onboarding, manage your entire recruitment process in one place. Find, evaluate, and hire top talent faster."
        primaryCTA={{ text: "Post Your First Job", link: "/sign-up" }}
        secondaryCTA={{ text: "See How It Works", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Complete Recruitment Solution"
        subtitle="Everything you need to attract, evaluate, and hire great talent"
      >
        <FeatureGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Hire better, faster with a streamlined process"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Organized Candidate Pipeline"
          description="View all applications in a clean, organized dashboard. Filter by status, score, or application date. Leave notes, rate candidates, and collaborate with your team to make better hiring decisions. Track where each candidate is in your process at a glance."
        />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Data-Driven Hiring Decisions"
          description="See how your recruitment efforts are performing with detailed analytics. Track application sources, measure time-to-hire, identify bottlenecks in your process, and optimize your job postings based on real data. Make smarter hiring decisions with actionable insights."
          imagePosition="left"
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="Build a complete HR solution"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Find Your Next Great Hire"
          description="Stop juggling spreadsheets and email. Manage your entire recruitment process in one powerful platform."
          primaryCTA={{ text: "Start Hiring Today", link: "/sign-up" }}
          secondaryCTA={{ text: "Request a Demo", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
